import { F_MapMarkerPolygonFragment } from '__generated__v2/graphql';
import { isNil, omitBy } from 'lodash';
import { FillLayout, FillPaint, LineLayout, LinePaint } from 'mapbox-gl';
import { EmptyLayer } from 'pages/special/OsloDesignFair/map/markers/view/EmptyLayer';
import { RefObject } from 'react';
import { Layer, MapRef, Source } from 'react-map-gl';

interface Props {
    marker: F_MapMarkerPolygonFragment;
    beforeId?: string;
    mapRef: RefObject<MapRef | null>;

    isHighlighted?: boolean;
    orderIndex: number;
}

export const PolygonMarker = (props: Props): JSX.Element => {
    const marker = props.marker;
    const markerId = props.marker.id;

    const closedCoordinates = [...marker.coordinates, marker.coordinates[0]].filter(Boolean);

    const fillPaintAttributes: FillPaint = {
        'fill-color': marker.fillColor,
        'fill-opacity': marker.fillOpacity
    };
    const fillLayoutAttributes: FillLayout = {
        visibility: !props.marker.isHidden && props.marker.isFilled ? 'visible' : 'none'
    };

    const borderPaintAttributes: LinePaint = {
        'line-color': marker.borderColor,
        'line-opacity': marker.borderOpacity,
        'line-width': marker.borderWidth
    };
    const borderLayoutAttributes: LineLayout = {
        visibility: !props.marker.isHidden ? 'visible' : 'none'
    };

    const layerIds = {
        layer: markerId + '|layer',
        layerClick: markerId + '|clickable',
        border: markerId + '|layer-border',
        borderClick: markerId + '|clickable-border',
        highlight: markerId + '|highlight',
        last: markerId + '|last'
    };

    const beforeIds = {
        layer: props.beforeId,
        layerClick: layerIds.layer,
        border: layerIds.layerClick,
        borderClick: layerIds.border,
        highlight: layerIds.borderClick,
        last: layerIds.highlight
    };

    return (
        <Source
            id={markerId}
            type="geojson"
            data={{
                type: 'Feature',
                properties: {
                    orderIndex: props.orderIndex,
                    markerId
                },
                geometry: {
                    type: 'Polygon',
                    coordinates: [closedCoordinates.map(({ lng, lat }) => [lng, lat])]
                }
            }}
        >
            <div>
                <Layer
                    id={layerIds.layer}
                    beforeId={beforeIds.layer}
                    type="fill"
                    source={`${markerId}`}
                    paint={{ ...omitBy(fillPaintAttributes, isNil) }}
                    layout={{ ...omitBy(fillLayoutAttributes, isNil) }}
                />
                {props.marker.selectable ? (
                    <Layer
                        id={layerIds.layerClick}
                        beforeId={beforeIds.layerClick}
                        type="fill"
                        source={`${markerId}`}
                        paint={{
                            'fill-opacity': 0
                        }}
                    />
                ) : (
                    <EmptyLayer id={layerIds.layerClick} beforeId={beforeIds.layerClick} />
                )}
                {marker.hasBorder ? (
                    <Layer
                        id={layerIds.border}
                        type="line"
                        beforeId={beforeIds.border}
                        source={`${markerId}`}
                        paint={{ ...omitBy(borderPaintAttributes, isNil) }}
                        layout={{ ...omitBy(borderLayoutAttributes, isNil) }}
                    />
                ) : (
                    <EmptyLayer id={layerIds.border} beforeId={beforeIds.border} />
                )}
                {marker.hasBorder && marker.selectable ? (
                    <Layer
                        id={layerIds.borderClick}
                        type="line"
                        beforeId={beforeIds.borderClick}
                        source={`${markerId}`}
                        paint={{
                            ...omitBy(borderPaintAttributes, isNil),
                            'line-opacity': 0
                        }}
                        layout={{ ...omitBy(borderLayoutAttributes, isNil) }}
                    />
                ) : (
                    <EmptyLayer id={layerIds.borderClick} beforeId={beforeIds.borderClick} />
                )}
                {props.isHighlighted ? (
                    <Layer
                        id={layerIds.highlight}
                        beforeId={beforeIds.highlight}
                        type="line"
                        source={markerId}
                        paint={{
                            ...omitBy(borderPaintAttributes, isNil),
                            'line-width': Math.max(
                                Number(borderPaintAttributes['line-width']) / 3,
                                6
                            ),
                            'line-color': '#F8E71C',
                            'line-offset': -Number(borderPaintAttributes['line-width']) / 2,
                            'line-opacity': 1,
                            'line-blur': 4
                        }}
                        layout={{ ...omitBy(borderLayoutAttributes, isNil) }}
                    />
                ) : (
                    <EmptyLayer id={layerIds.highlight} beforeId={beforeIds.highlight} />
                )}
                <EmptyLayer id={layerIds.last} beforeId={beforeIds.last} />
            </div>
        </Source>
    );
};
